import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

interface VideoState {
  broadcasts: Record<string, {
    id: string;
    status: string;
    roomId: string;
    signalingServer: string;
    participants: any[];
    localStream: MediaStream | null;
    remoteStreams: any[];
  }>;
  isConnecting: Record<string, boolean>;
  error: string | null;
}

const initialState: VideoState = {
  broadcasts: {},
  isConnecting: {},
  error: null,
};

export const startBroadcast = createAsyncThunk(
  'video/startBroadcast',
  async (projectId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/video/start`,
      {},
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return { projectId, broadcast: response.data.data.broadcast };
  }
);

export const joinBroadcast = createAsyncThunk(
  'video/joinBroadcast',
  async (projectId: string, { getState }) => {
    const state = getState() as { auth: { accessToken: string | null } };
    const response = await axios.post(
      `${API_BASE_URL}/projects/${projectId}/video/join`,
      {},
      { headers: { Authorization: `Bearer ${state.auth.accessToken}` } }
    );
    return { projectId, broadcast: response.data.data.broadcast };
  }
);

const videoSlice = createSlice({
  name: 'video',
  initialState,
  reducers: {
    setLocalStream: (state, action: PayloadAction<{ projectId: string; stream: MediaStream | null }>) => {
      if (state.broadcasts[action.payload.projectId]) {
        state.broadcasts[action.payload.projectId].localStream = action.payload.stream;
      }
    },
    addRemoteStream: (state, action: PayloadAction<{ projectId: string; stream: any }>) => {
      if (state.broadcasts[action.payload.projectId]) {
        state.broadcasts[action.payload.projectId].remoteStreams.push(action.payload.stream);
      }
    },
    removeRemoteStream: (state, action: PayloadAction<{ projectId: string; streamId: string }>) => {
      if (state.broadcasts[action.payload.projectId]) {
        state.broadcasts[action.payload.projectId].remoteStreams = state.broadcasts[action.payload.projectId]
          .remoteStreams.filter((s: any) => s.id !== action.payload.streamId);
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startBroadcast.pending, (state, action) => {
        state.isConnecting[action.meta.arg] = true;
      })
      .addCase(startBroadcast.fulfilled, (state, action) => {
        state.isConnecting[action.payload.projectId] = false;
        state.broadcasts[action.payload.projectId] = {
          ...action.payload.broadcast,
          localStream: null,
          remoteStreams: [],
        };
      })
      .addCase(joinBroadcast.pending, (state, action) => {
        state.isConnecting[action.meta.arg] = true;
      })
      .addCase(joinBroadcast.fulfilled, (state, action) => {
        state.isConnecting[action.payload.projectId] = false;
        state.broadcasts[action.payload.projectId] = {
          ...action.payload.broadcast,
          localStream: null,
          remoteStreams: [],
        };
      });
  },
});

export const { setLocalStream, addRemoteStream, removeRemoteStream, setError } = videoSlice.actions;
export default videoSlice.reducer;

